import React, { useEffect, useState } from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './NodeLoadingState.css';

export const NodeRequestFeedback = ({ startedAt, retryAt, attempt, hasCachedNodes = false }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAt, retryAt]);

  const waiting = attempt > 0;
  const seconds = waiting
    ? Math.max(0, Math.ceil((retryAt - now) / 1000))
    : Math.max(0, Math.floor((now - startedAt) / 1000));

  return (
    <div className={`fleet-request-feedback mb-5 overflow-hidden rounded-xl border ${waiting ? 'border-amber-200 bg-amber-50/80 text-amber-800' : 'border-accent-200 bg-accent-50/70 text-accent-800'}`}>
      <div className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
        <div aria-hidden="true" className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border bg-white sm:h-12 sm:w-12 ${waiting ? 'border-amber-200' : 'border-accent-200'}`}>
          {waiting ? <Clock className="h-5 w-5" /> : <RefreshCw className="fleet-loading-spinner h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p role="status" aria-live="polite" className="text-sm font-medium">
            {waiting ? t(hasCachedNodes ? 'aiFleet.nodeRequest.waitingWithCache' : 'aiFleet.nodeRequest.waiting', { attempt, max: 3 }) : t('aiFleet.nodeRequest.loadingTitle')}
          </p>
          <p className="mt-1 text-xs leading-5 opacity-80">
            {waiting ? t('aiFleet.nodeRequest.retryCountdown', { seconds }) : t(seconds >= 10 ? 'aiFleet.nodeRequest.loadingSlow' : 'aiFleet.nodeRequest.loadingHint')}
          </p>
        </div>
        {!waiting && <span className="flex-shrink-0 text-xs tabular-nums opacity-70">{t('aiFleet.nodeRequest.elapsed', { seconds })}</span>}
      </div>
      <div aria-hidden="true" className={`h-0.5 overflow-hidden ${waiting ? 'bg-amber-100' : 'bg-accent-100'}`}>
        {waiting ? <div className="fleet-retry-progress h-full bg-amber-400" style={{ width: `${Math.min(100, seconds / 60 * 100)}%` }} />
          : <div className="fleet-loading-progress h-full w-1/3 rounded-full bg-accent-400" />}
      </div>
    </div>
  );
};

export const NodeLoadingSkeleton = ({ isPlus }) => (
  <div aria-hidden="true" className={`grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 ${isPlus ? 'lg:grid-cols-4 xl:grid-cols-5' : 'md:grid-cols-4 lg:grid-cols-5'}`}>
    {Array.from({ length: 10 }, (_, index) => (
      <div key={index} className="fleet-node-skeleton flex min-w-0 flex-col gap-4 rounded-xl border border-ink-100 bg-white p-3 sm:p-4" style={{ animationDelay: `${index * 45}ms` }}>
        <div className="flex items-center justify-between gap-3">
          <div className="fleet-skeleton-block h-8 w-8 rounded-lg" />
          <div className="fleet-skeleton-block h-5 w-10 rounded" />
        </div>
        <div className="fleet-skeleton-block h-4 w-4/5 rounded" />
        <div className="fleet-skeleton-block h-5 w-12 rounded-full" />
        <div className="fleet-skeleton-block h-3 w-2/3 rounded" />
        <div className="space-y-3 border-t border-ink-100 pt-3">
          <div className="fleet-skeleton-block h-3 w-full rounded" />
          <div className="fleet-skeleton-block h-3 w-3/4 rounded" />
        </div>
        <div className="fleet-skeleton-block mt-auto h-10 w-full rounded-lg" />
      </div>
    ))}
  </div>
);
